import React, { useState } from "react";
import { Form, Input, Button, Typography, ConfigProvider, App } from "antd";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import {
  changeOwnPasswordAction,
  fetchUserDataAction,
} from "../../slices/authSlice";
import { PasswordRequirements } from "../../components/auth/PasswordRequirements";
import { usePasswordPolicy } from "../../hooks/usePasswordPolicy";
import LoginImage from "../../assets/login_image.webp";

const { Title, Paragraph } = Typography;

// Authenticated force-change screen shown when an admin reset the user's
// password (userData.force_password_change). On success it re-fetches the
// profile (so the now-cleared flag is authoritative) and drops the user into
// the app. ProtectedRoute Gate 4 keeps the user here until this completes.
const ForcePasswordChangePage = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [form] = Form.useForm();
  const { message } = App.useApp();
  const [loading, setLoading] = useState(false);
  const [passwordValue, setPasswordValue] = useState("");
  const { rules: passwordRules, formRules: passwordFormRules } =
    usePasswordPolicy();

  const onFinish = async (values) => {
    setLoading(true);
    try {
      await dispatch(
        changeOwnPasswordAction({ newPassword: values.password })
      ).unwrap();
      // Re-fetch the profile so the now-cleared force_password_change flag is
      // authoritative before ProtectedRoute re-evaluates on navigation.
      await dispatch(fetchUserDataAction({ withScope: true })).unwrap();
      message.success({ content: "Password updated. Welcome back!", duration: 3 });
      navigate("/", { replace: true });
    } catch (error) {
      message.error({
        content: error || "Failed to update password. Please try again.",
        duration: 5,
      });
    } finally {
      setLoading(false);
    }
  };

  const onFinishFailed = () => {
    message.error({ content: "Please check your inputs.", duration: 3 });
  };

  return (
    <div className="flex justify-center items-center h-[100vh] w-[100vw] relative bg-white">
      <div className="absolute top-[1.625rem] left-[1.625rem] text-[#1D2939] text-[1rem] font-normal tracking-[1.2px]">
        Dash
        <span className="text-[1.25rem] font-semibold tracking-normal">ONE</span>
      </div>

      <div className="flex justify-center items-center flex-1 flex-col gap-[2rem] px-4">
        <div className="w-full max-w-[31.625rem] flex flex-col">
          <div className="w-full flex flex-col gap-[40px]">
            <div className="flex flex-col gap-[12px]">
              <Title
                level={2}
                className="!m-0 !text-[30px] !font-bold !text-[rgba(0,0,0,0.85)] !leading-[40px]"
              >
                Set a new password
              </Title>
              <Paragraph className="!m-0 !text-[15px] !leading-[24px] !text-[rgba(0,0,0,0.65)] font-normal">
                An administrator reset your password. Please set a new one to continue to DashONE.
              </Paragraph>
            </div>

            <ConfigProvider
              theme={{
                token: {
                  colorPrimary: "#0E49B5",
                  borderRadius: 24,
                },
              }}
            >
              <Form
                form={form}
                name="force-password-change"
                onFinish={onFinish}
                onFinishFailed={onFinishFailed}
                autoComplete="off"
                layout="vertical"
                className="w-full flex flex-col gap-[24px]"
              >
                <Form.Item
                  label={
                    <span className="text-[14px] font-medium text-[#3f3f46] leading-[24px] px-[6px]">
                      New Password
                    </span>
                  }
                  name="password"
                  rules={[
                    { required: true, message: "Please input your password!" },
                    ...passwordFormRules,
                  ]}
                  className="!mb-0"
                >
                  <Input.Password
                    placeholder="New Password"
                    className="!rounded-[24px] !border-[#d9d9d9] !px-4 !py-[6px] !h-auto"
                    style={{ fontSize: "14px", lineHeight: "22px", fontFamily: "Inter" }}
                    onChange={(e) => setPasswordValue(e.target.value)}
                  />
                </Form.Item>
                <PasswordRequirements
                  password={passwordValue}
                  rules={passwordRules}
                />

                <Form.Item
                  label={
                    <span className="text-[14px] font-medium text-[#3f3f46] leading-[24px] px-[6px]">
                      Confirm Password
                    </span>
                  }
                  name="confirmPassword"
                  dependencies={["password"]}
                  rules={[
                    {
                      required: true,
                      message: "Please confirm your password!",
                    },
                    ({ getFieldValue }) => ({
                      validator(_, value) {
                        if (!value || getFieldValue("password") === value) {
                          return Promise.resolve();
                        }
                        return Promise.reject(
                          new Error("The two passwords do not match.")
                        );
                      },
                    }),
                  ]}
                  className="!mb-0"
                >
                  <Input.Password
                    placeholder="Confirm Password"
                    className="!rounded-[24px] !border-[#d9d9d9] !px-4 !py-[6px] !h-auto"
                    style={{
                      fontSize: "14px",
                      lineHeight: "22px",
                      fontFamily: "Inter",
                    }}
                  />
                </Form.Item>
              </Form>
            </ConfigProvider>

            <div className="w-full h-[36px]">
              <Button
                type="primary"
                htmlType="submit"
                loading={loading}
                onClick={() => form.submit()}
                className="w-full h-full !rounded-[24px] !text-[16px] !font-normal !leading-[24px]"
              >
                Set new password
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="absolute bottom-[2rem] left-[1.625rem] text-[rgb(71,84,103)] text-[0.75rem] font-[400]">
        @DashONE 2025
      </div>
      <div
        className="flex-1 h-full w-[50vw] self-stretch rounded-[20px_0_0_20px] bg-no-repeat bg-center bg-cover hidden md:block"
        style={{ backgroundImage: `url(${LoginImage})` }}
      />
    </div>
  );
};

export default ForcePasswordChangePage;
